import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Field, inputCls } from "@/components/form";
import { useAuth, roleHome } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Willkommen zurück, ${user.name}`);
      navigate(roleHome(user.role));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-navy-900 flex items-center justify-center px-6 relative">
      <Seo title="Login" path="/login" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.08),transparent_50%)]" />
      <Link to="/" className="absolute top-8 left-8 text-slate-400 hover:text-gold-500 flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Zur Website</Link>
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><LogoMark size={56} /></div>
          <span className="font-display font-extrabold text-white text-2xl tracking-tight">VM-FFM<span className="text-gold-500">.DE</span></span>
          <p className="font-brand text-slate-400 text-sm mt-2 tracking-wide">Anmeldung für Mitarbeiter & Kunden</p>
        </div>
        <form data-testid="login-form" onSubmit={submit} className="bg-navy-800 border border-white/10 rounded-md p-8 space-y-5">
          <Field label="E-Mail"><input data-testid="login-email" required type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@vm-ffm.de" /></Field>
          <Field label="Passwort"><input data-testid="login-password" required type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></Field>
          <button data-testid="login-submit" disabled={loading} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-3.5 flex items-center justify-center gap-2 hover:bg-gold-300 transition-colors disabled:opacity-60">
            {loading ? "Anmelden…" : <>Anmelden <LogIn size={16} /></>}
          </button>
          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">oder</span><span className="flex-1 h-px bg-white/10" />
          </div>
          <button type="button" data-testid="google-login-btn" onClick={googleLogin} className="w-full bg-white text-navy-900 font-medium rounded-sm py-3.5 flex items-center justify-center gap-3 hover:bg-slate-100 transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.85 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.67-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.67 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Mit Google anmelden
          </button>
        </form>
      </div>
    </div>
  );
}
